import { Translation, Translations, Artist, Member } from '../../types/response/ISearchArtistResponse';

interface SearchResult {
  artists: Artist[];
  members: Array<{
    member: Member;
    artist: Artist;
  }>;
}

// 검색 함수
export function searchArtistAndMember(query: string, allArtists: Artist[]): SearchResult {
  const result: SearchResult = {
    artists: [],
    members: []
  };

  if (!query || query.trim() === '') {
    return result;
  }

  const searchQuery = query.toLowerCase().trim();
  const foundArtistIds = new Set<string>();
  
  // 모든 Artist 순회
  allArtists.forEach((artist: any) => {
    let artistMatched = false;

    // Artist 이름 검색
    if (artist.name.toLowerCase().includes(searchQuery)) {
      artistMatched = true;
    }

    // Artist 짧은 이름 검색
    if (artist.shortName.toLowerCase().includes(searchQuery)) {
      artistMatched = true;
    }

    // Artist 별명 검색
    if (artist.aliasNames?.some((alias: string) => alias.toLowerCase().includes(searchQuery))) {
      artistMatched = true;
    }

    // Artist translations 검색
    if (artist.translations) {
      const translationValues = Object.values(artist.translations).flat();
      translationValues.forEach(translationObj => {
        if (typeof translationObj === 'object' && translationObj !== null) {
          Object.values(translationObj).forEach(value => {
            if (typeof value === 'string' && value.toLowerCase().includes(searchQuery)) {
              artistMatched = true;
            }
          });
        }
      });
    }

    // Artist가 매치되면 결과에 추가
    if (artistMatched && !foundArtistIds.has(artist.artistId)) {
      result.artists.push(artist);
      foundArtistIds.add(artist.artistId);
    }

    // Member 검색
    artist.members.forEach((member: any) => {
      let memberMatched = false;

      // Member 이름 검색
      if (member.name.toLowerCase().includes(searchQuery)) {
        memberMatched = true;
      }

      // Member 별명 검색
      if (member.aliasNames?.some((alias: string) => alias.toLowerCase().includes(searchQuery))) {
        memberMatched = true;
      }

      // Member translations 검색
      if (member.translations && member.translations.name) {
        Object.values(member.translations.name).forEach(value => {
          if (typeof value === 'string' && value.toLowerCase().includes(searchQuery)) {
            memberMatched = true;
          }
        });
      }

      // Member가 매치되면 결과에 추가
      if (memberMatched) {
        result.members.push({
          member,
          artist
        });
      }
    });
  });

  return result;
}

// 특정 Artist 검색
export function searchArtist(query: string, allArtists: Artist[]): Artist[] {
  const result = searchArtistAndMember(query, allArtists);
  return result.artists;
}

// 특정 Member 검색
export function searchMember(query: string, allArtists: Artist[]): Array<{ member: Member; artist: Artist }> {
  const result = searchArtistAndMember(query, allArtists);
  return result.members;
}

// Artist ID로 Artist 찾기
export function findArtistById(artistId: string, allArtists: Artist[]): Artist | undefined {
  const result = searchArtistAndMember(artistId, allArtists);
  return result.artists.find(artist => artist.artistId === artistId);
}

// Member ID와 Artist ID로 Member 찾기
export function findMemberById(artistId: string, memberId: string, allArtists: Artist[]): Member | undefined {
  const artist = findArtistById(artistId, allArtists);
  return artist?.members.find(member => member.memberId === memberId);
}

// 모든 Artist 반환
export function getAllArtists(allArtists: Artist[]): Artist[] {
  const result = searchArtistAndMember('', allArtists);
  return result.artists;
}

// 특정 Artist의 모든 Member 반환
export function getArtistMembers(artistId: string, allArtists: Artist[]): Member[] {
  const artist = findArtistById(artistId, allArtists);
  return artist?.members || [];
}
